// BoardManagement — Sprint 15 orchestrator. Final sprint of the
// god-component decomposition series. Now a thin layout shell:
//  * data + persistence  → useBoardData (11 store-bound mutations)
//  * pure helpers        → boardTypes (palettes + number generators)
//  * presentation        → BoardStatsRow + BoardMembersTab +
//                          BoardMeetingsTab + BoardResolutionsTab
//  * 4 modals            → BoardModals (Member / Meeting /
//                          Resolution / Minutes)
import React, { useMemo, useState } from 'react';
import { Form, Tabs, Typography } from 'antd';
import {
  BankOutlined, BarChartOutlined, TeamOutlined, VideoCameraOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useAuthStore } from '../store/authStore';
import type { BoardMember, BoardMeeting, BoardResolution, MinuteItem } from '../types';
import { useBoardData }       from './board/useBoardData';
import { BoardStatsRow }      from './board/BoardStatsRow';
import { BoardMembersTab }    from './board/BoardMembersTab';
import { BoardMeetingsTab }   from './board/BoardMeetingsTab';
import { BoardResolutionsTab } from './board/BoardResolutionsTab';
import {
  MemberEditModal, MeetingEditModal, ResolutionEditModal, MeetingMinutesModal,
} from './board/BoardModals';
import { genMeetingNumber, genVoteNumber, isScheduledStatus } from './board/boardTypes';

dayjs.extend(isSameOrAfter);

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const BoardManagement: React.FC = () => {
  const { currentCondo } = useAuthStore();

  const {
    owners, boardMembers, boardMeetings, boardResolutions,
    saveMember, deleteMember,
    saveMeeting, deleteMeeting, appendMinutes,
    saveResolution, closeResolution, deleteResolution, castVote,
  } = useBoardData({ condoId: currentCondo?.id });

  // ── Transient UI state ──────────────────────────────────────────────
  const [memberOpen,  setMemberOpen]  = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [voteOpen,    setVoteOpen]    = useState(false);
  const [minutesOpen, setMinutesOpen] = useState(false);

  const [editingMember, setEditingMember] = useState<BoardMember | null>(null);
  const [editingMeeting, setEditingMeeting] = useState<BoardMeeting | null>(null);
  const [editingVote, setEditingVote]      = useState<BoardResolution | null>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<BoardMeeting | null>(null);
  const [minutesText, setMinutesText] = useState('');

  const [memberForm]  = Form.useForm();
  const [meetingForm] = Form.useForm();
  const [voteForm]    = Form.useForm();

  // ── Stats ───────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    totalMeetings:   boardMeetings.length,
    upcoming:        boardMeetings.filter((m: BoardMeeting) =>
      isScheduledStatus(m.status) && dayjs(m.date).isSameOrAfter(dayjs(), 'day'),
    ).length,
    openVotes:       boardResolutions.filter((v: BoardResolution) => v.status === 'open').length,
    totalVotes:      boardResolutions.length,
  }), [boardMeetings, boardResolutions]);

  // ── Member modal openers + submit ───────────────────────────────────
  const openAddMember = () => {
    setEditingMember(null);
    memberForm.resetFields();
    memberForm.setFieldsValue({
      role:      'Director',
      termStart: dayjs().format('YYYY-MM-DD'),
      termEnd:   dayjs().add(2, 'year').format('YYYY-MM-DD'),
      isActive:  true,
    });
    setMemberOpen(true);
  };

  const openEditMember = (m: BoardMember) => {
    setEditingMember(m);
    memberForm.setFieldsValue(m);
    setMemberOpen(true);
  };

  const handleSubmitMember = async () => {
    try {
      const values = await memberForm.validateFields();
      await saveMember(values, editingMember?.id);
      setMemberOpen(false);
    } catch (err: any) {
      // Field-level validation errors stay inline; data-layer failures
      // already toasted by the hook.
      if (err?.errorFields) return;
    }
  };

  // ── Meeting modal openers + submit ──────────────────────────────────
  const openAddMeeting = () => {
    setEditingMeeting(null);
    meetingForm.resetFields();
    meetingForm.setFieldsValue({
      meetingNumber:  genMeetingNumber(),
      date:           dayjs().add(7, 'day').format('YYYY-MM-DD'),
      startTime:      '19:00',
      type:           'board',
      status:         'scheduled',
      quorumRequired: Math.ceil(owners.length / 2),
      location:       'Party Room / Virtual',
    });
    setMeetingOpen(true);
  };

  const openEditMeeting = (m: BoardMeeting) => {
    setEditingMeeting(m);
    meetingForm.setFieldsValue(m);
    setMeetingOpen(true);
  };

  const handleSubmitMeeting = async () => {
    try {
      const values = await meetingForm.validateFields();
      await saveMeeting(values, editingMeeting);
      setMeetingOpen(false);
    } catch (err: any) {
      if (err?.errorFields) return;
    }
  };

  // ── Resolution modal openers + submit ───────────────────────────────
  const openAddVote = () => {
    setEditingVote(null);
    voteForm.resetFields();
    voteForm.setFieldsValue({
      voteNumber:    genVoteNumber(),
      openDate:      dayjs().format('YYYY-MM-DD'),
      closeDate:     dayjs().add(7, 'day').format('YYYY-MM-DD'),
      status:        'open',
      votesFor:      0,
      votesAgainst:  0,
      votesAbstain:  0,
      totalEligible: owners.length,
      category:      'Resolution',
    });
    setVoteOpen(true);
  };

  const openEditVote = (v: BoardResolution) => {
    setEditingVote(v);
    voteForm.setFieldsValue(v);
    setVoteOpen(true);
  };

  const handleSubmitVote = async () => {
    try {
      const values = await voteForm.validateFields();
      await saveResolution(values, editingVote?.id);
      setVoteOpen(false);
    } catch (err: any) {
      if (err?.errorFields) return;
    }
  };

  // ── Minutes ─────────────────────────────────────────────────────────
  const openMinutes = (meeting: BoardMeeting) => {
    setSelectedMeeting(meeting);
    setMinutesText('');
    setMinutesOpen(true);
  };

  const handleAddMinutesEntry = async () => {
    if (!selectedMeeting || !minutesText.trim()) return;
    const item: MinuteItem = {
      id:          `min-${Date.now()}`,
      title:       'General Notes',
      content:     minutesText,
      actionItems: [],
    };
    try {
      const updated = await appendMinutes(selectedMeeting, item);
      setSelectedMeeting(updated);
      setMinutesText('');
    } catch { /* hook already toasted */ }
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          <BankOutlined style={{ marginRight: 10, color: '#2f54eb' }} />
          Board Management
        </Title>
        <Text type="secondary">
          Schedule meetings, record minutes, and manage board resolutions &amp; voting
        </Text>
      </div>

      <BoardStatsRow
        activeMembers={boardMembers.filter((m: BoardMember) => m.isActive).length}
        upcomingMeetings={stats.upcoming}
        openVotes={stats.openVotes}
        totalResolutions={stats.totalVotes}
      />

      <Tabs defaultActiveKey="members">
        <TabPane tab={<span><TeamOutlined /> Board Members</span>} key="members">
          <BoardMembersTab
            members={boardMembers}
            onAdd={openAddMember}
            onEdit={openEditMember}
            onDelete={deleteMember}
          />
        </TabPane>

        <TabPane tab={<span><VideoCameraOutlined /> Meetings</span>} key="meetings">
          <BoardMeetingsTab
            meetings={boardMeetings}
            onAdd={openAddMeeting}
            onEdit={openEditMeeting}
            onDelete={deleteMeeting}
            onOpenMinutes={openMinutes}
          />
        </TabPane>

        <TabPane tab={<span><BarChartOutlined /> Resolutions &amp; Voting</span>} key="votes">
          <BoardResolutionsTab
            resolutions={boardResolutions}
            onAdd={openAddVote}
            onEdit={openEditVote}
            onDelete={deleteResolution}
            onCastVote={castVote}
            onClose={closeResolution}
          />
        </TabPane>
      </Tabs>

      <MemberEditModal
        open={memberOpen}
        editingMember={editingMember}
        form={memberForm}
        onCancel={() => setMemberOpen(false)}
        onSubmit={handleSubmitMember}
      />
      <MeetingEditModal
        open={meetingOpen}
        editingMeeting={editingMeeting}
        form={meetingForm}
        onCancel={() => setMeetingOpen(false)}
        onSubmit={handleSubmitMeeting}
      />
      <ResolutionEditModal
        open={voteOpen}
        editingVote={editingVote}
        form={voteForm}
        onCancel={() => setVoteOpen(false)}
        onSubmit={handleSubmitVote}
      />
      <MeetingMinutesModal
        open={minutesOpen}
        meeting={selectedMeeting}
        text={minutesText}
        onTextChange={setMinutesText}
        onClose={() => setMinutesOpen(false)}
        onAddEntry={handleAddMinutesEntry}
      />
    </div>
  );
};

export default BoardManagement;
